import React, { useState, useEffect, useRef } from "react";
import Webcam from "react-webcam";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import Cookies from "universal-cookie";
import { useTranslation } from "react-i18next";

import { useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import useMediaQuery from "@mui/material/useMediaQuery";

import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import PublishIcon from "@mui/icons-material/Publish";

import { jsPDF } from "jspdf";
import InstructionHeader from "./InstructionHeader";

const useStyles = makeStyles((theme) => ({
  labelToBtn: {
    backgroundColor: "#1c355e",
    color: "#fff",
    cursor: "pointer",
    outline: 0,
    border: 0,
    margin: 0,
    justifyContent: "center",
    verticalAlign: "middle",
    font: "400 13.3333px Arial",
    lineHeight: 1.75,
    letterSpacing: "0.02857em",
    textTransform: "uppercase",
    padding: "10px 16px",
    borderRadius: 4,
    "&:hover": {
      backgroundColor: "#132541",
      boxShadow:
        "0px 2px 4px -1px rgb(0 0 0 / 20%), 0px 4px 5px 0px rgb(0 0 0 / 14%), 0px 1px 10px 0px rgb(0 0 0 / 12%)",
    },
  },
  root: {
    flexGrow: 1,
    [theme.breakpoints.up("sm")]: { width: "80vw", maxWidth: 800 },
    [theme.breakpoints.down("sm")]: { maxWidth: 300 },
  },
  pdfContainer: {
    [theme.breakpoints.up("sm")]: { width: "80vw", maxWidth: 800 },
    [theme.breakpoints.down("sm")]: { width: "60vw", maxWidth: 300 },
  },
  customToolbar: {
    backgroundColor: theme.palette.background.paper,
  },
  actionButton: {
    backgroundColor: "#76BC21",
    "&:hover": {
      backgroundColor: "#edae49",
    },
    color: "white",
    marginTop: 5,
    fontWeight: 600,
  },
  tabButton: {
    [theme.breakpoints.up("sm")]: {
      paddingBottom: "20px",
      paddingLeft: "20px",
    },
    [theme.breakpoints.down("sm")]: {
      padding: "10px",
      textAlign: "center",
    },
  },
}));

const initialFieldValues = {
  userId: 0,
  clientID: 0,
  uploadTypeID: 0,
  uploadFile: null,
};

const TakePhoto = ({
  selectedUploadTypeID,
  selectedUploadName,
  handleUploadDocument,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [imageData, setImageData] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [fileName, setFileName] = useState();
  const [values, setValues] = useState(initialFieldValues);
  const webcamRef = useRef(null);
  // const [imgSrc, setImgSrc] = useState(null);
  const [isFileFromWebcam, setIsFileFromWebcam] = useState(false);
  const [fileAsBlob, setFileAsBlob] = useState(null);
  const cookies = new Cookies();
  const user = cookies.get("lifesaferUser");
  const maxFileSize = 10 * 1024 * 1024;

  useEffect(() => {
    if (
      values != initialFieldValues &&
      values.userId != 0 &&
      values.clientID != 0 &&
      values.uploadTypeID != 0 &&
      values.uploadFile != null
    ) {
      sendFile();
    }
  }, [values]);

  const uploadClick = (uploadType) => {
    setSubmitting(true);
    let fileUpload;
    if (uploadType === "Photo" || uploadType === "PhotoFromWebcam") {
      fileUpload = imageData.imageFile;
      // } else {
      //   fileUpload = file.imageFile;
    }

    setValues({
      ...values,
      userId: user.userId,
      clientID: user.clientID,
      uploadTypeID: selectedUploadTypeID,
      uploadFile: fileUpload,
    });
  };

  const sendFile = () => {
    setSubmitting(true);
    let errMsg = validate();
    if (errMsg === "") {
      let formData = new FormData();
      if (
        isFileFromWebcam === false &&
        values.uploadFile.type !== "image/png"
      ) {
        formData.append("uploadFile", values.uploadFile, fileName);
      } else {
        formData.append("uploadFile", fileAsBlob, fileName);
      }

      let uploadDocumentData = {
        userId: values.userId,
        clientID: values.clientID,
        uploadTypeID: values.uploadTypeID,
        uploadFile: formData,
      };
      handleUploadDocument(uploadDocumentData);
    } else {
      toast.error(t("documents.documentUploadFailed") + ": " + errMsg);
    }
    setSubmitting(false);
  };

  const validate = () => {
    let errorText = "";
    let tempValues = {};
    tempValues.uploadTypeID = values.uploadTypeID <= 0 ? false : true;
    tempValues.fileName = fileName == "" ? false : true;
    tempValues.correctFileExtension =
      isPdf(fileName) || values.uploadFile.type == "image/png" ? true : false;
    tempValues.fileSize = values.uploadFile.size >= maxFileSize ? false : true;

    if (tempValues.uploadTypeID === false) {
      errorText = t("documents.documentUploadErrorUploadType");
    } else if (tempValues.correctFileExtension === false) {
      errorText = t("documents.documentUploadErrorFileType");
    } else if (tempValues.fileSize === false) {
      errorText = t("documents.documentUploadErrorFileSize");
    } else if (Object.values(tempValues).every((x) => x == true) === false) {
      errorText =
        t("documents.documentUploadFailed") +
        ": " +
        t("documents.documentUploadDataError");
    }

    return errorText;
  };

  function isPdf(filename) {
    var ext = getExtension(filename);
    switch (ext.toLowerCase()) {
      case "pdf":
        return true;
    }
    return false;
  }

  function getExtension(filename) {
    var parts = filename.split(".");
    return parts[parts.length - 1];
  }

  // const videoConstraints = {
  //   width: 1280,
  //   height: 720,
  //   facingMode: "user",
  // };

  // const WebcamCapture = () => {
  //   const theme = useTheme();
  //   const isXtraSmall = useMediaQuery(theme.breakpoints.down('sm'));
  //   const capture = React.useCallback(() => {
  //     const imageSrc = webcamRef.current.getScreenshot();
  //     setImgSrc(imageSrc);
  //     setFileName(selectedUploadName + ".pdf");
  //     let iWidth = webcamRef.current.canvas.width;
  //     let iHeight = webcamRef.current.canvas.height;

  //     let doc = new jsPDF("p", "px", "letter");
  //     let maxWidth = doc.internal.pageSize.getWidth();
  //     let maxHeight = doc.internal.pageSize.getHeight();
  //     let ratio = Math.min(maxWidth / iWidth, maxHeight / iHeight);
  //     iWidth = iWidth * ratio;
  //     iHeight = iHeight * ratio;
  //     doc.addImage(imageSrc, "JPEG", 0, 0, iWidth, iHeight);
  //     //doc.save(webCamCaptureFileName);
  //     var dataSrc = doc.output("datauristring"); // base64 string
  //     var blobPDF = new Blob([doc.output("blob")], { type: "application/pdf" });
  //     setFileAsBlob(blobPDF);
  //     setIsFileFromWebcam(true);
  //     setImageData({
  //       imageFile: dataSrc,
  //       imageSrc: imageSrc,
  //     });
  //   }, [webcamRef, imgSrc]);

  //   return <>
  //     <Webcam
  //       audio={false}
  //       height={isXtraSmall ? 200 : 300}
  //       ref={webcamRef}
  //       screenshotFormat="image/jpeg"
  //       width={isXtraSmall ? 300 : 400}
  //       videoConstraints={videoConstraints}
  //     />
  //     <Grid
  //       container
  //       item
  //       style={{ paddingTop: 10, paddingBottom: 10 }}
  //       justifyContent="center"
  //     >
  //       <Button
  //         disabled={submitting}
  //         size="medium"
  //         variant="contained"
  //         color="primary"
  //         onClick={capture}
  //         startIcon={<PublishIcon style={{ fontSize: "1.4em" }} />}
  //       >
  //         {t("documents.photoUpload")}
  //       </Button>
  //     </Grid>
  //   </>;
  // };
  const theme = useTheme();
  const isXtraSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const capture = React.useCallback(() => {
    const imageSrc = webcamRef.current.getScreenshot();
    // if (!imageSrc) {
    //   toast.error("Unable to capture image. Please try again.");
    //   return;
    // }

    // setImgSrc(imageSrc);
    setFileName(selectedUploadName + ".pdf");

    const video = webcamRef.current.video;
    const iWidth = video.videoWidth;
    const iHeight = video.videoHeight;

    const doc = new jsPDF("p", "px", "letter");
    const maxWidth = doc.internal.pageSize.getWidth();
    const maxHeight = doc.internal.pageSize.getHeight();
    const ratio = Math.min(maxWidth / iWidth, maxHeight / iHeight);

    doc.addImage(imageSrc, "JPEG", 0, 0, iWidth * ratio, iHeight * ratio);

    const dataSrc = doc.output("datauristring");
    const blobPDF = new Blob([doc.output("blob")], { type: "application/pdf" });

    setFileAsBlob(blobPDF);
    setIsFileFromWebcam(true);
    setImageData({
      imageFile: dataSrc,
      imageSrc: imageSrc,
    });
  }, [selectedUploadName]);

  return (
    <>
      {/* <div className={classes.root}>
      <Grid container direction="row" justifyContent="center">
        <Grid item xs={12}>
          {imageData ? (
            <Grid item>
              <img
                src={imageData.imageSrc}
                alt="Picture"
                style={{ height: "100%", width: "100%" }}
              />
            </Grid>
          ) : (
            <>
              <Grid item style={{ padding: 10 }}>
                <InstructionHeader id="ih1" />
              </Grid>
              <Grid item>
                <Typography>{t("documents.photoInstructions")}</Typography>
              </Grid>
            </>
          )}
          {imageData ? (
            <Grid item>
              <Grid
                container
                direction="row"
                justifyContent="center"
                style={{ paddingTop: 10 }}
              >
                <Grid item className={classes.tabButton}>
                  <Button
                    disabled={submitting}
                    size="medium"
                    variant="contained"
                    color="primary"
                    onClick={() => setImageData(null)}
                    startIcon={
                      <RotateLeftIcon style={{ fontSize: "1.4em" }} />
                    }
                  >
                    {t("documents.retake")}
                  </Button>
                </Grid>
                <Grid item className={classes.tabButton}>
                  <Button
                    disabled={submitting}
                    size="medium"
                    variant="contained"
                    color="primary"
                    onClick={() => uploadClick("PhotoFromWebcam")}
                    startIcon={<PublishIcon style={{ fontSize: "1.4em" }} />}
                  >
                    {t("documents.upload")}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          ) : (
            <>
              <Grid container item justifyContent="center">
                <WebcamCapture />
              </Grid>
            </>
          )}
        </Grid>
      </Grid>
    </div> */}
      <div className={classes.root} style={{ mx:"auto" }}>
        <Grid container direction="row" justifyContent="center">
          <Grid item xs={12}>
            <Grid item style={{ padding: 10 }}>
              <InstructionHeader id="ih1" />
            </Grid>

            {!imageData && (
              <>
                <Typography style={{ textAlign: "center" }}>
                  {t("documents.photoInstructions")}
                </Typography>

                <Grid
                  container
                  justifyContent="center"
                  style={{ marginTop: 16 }}
                >
                  <Webcam
                    audio={false}
                    ref={webcamRef}
                    screenshotFormat="image/jpeg"
                    height={isXtraSmall ? 200 : 300}
                    width={isXtraSmall ? 300 : 400}
                    videoConstraints={{
                      width: 1280,
                      height: 720,
                      facingMode: "user",
                    }}
                    style={{ borderRadius: 8 }}
                  />
                </Grid>

                <Grid
                  container
                  justifyContent="center"
                  style={{ marginTop: 16 }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={submitting}
                    onClick={capture}
                    startIcon={<PublishIcon />}
                  >
                    {t("documents.photoUpload")}
                  </Button>
                </Grid>
              </>
            )}

            {imageData && (
              <>
                <Grid
                  container
                  justifyContent="center"
                  style={{ marginTop: 16 }}
                >
                  <img
                    src={imageData.imageSrc}
                    alt="Captured"
                    style={{ width: "100%", maxWidth: 400, borderRadius: 8 }}
                  />
                </Grid>

                <Grid
                  container
                  justifyContent="center"
                  spacing={2}
                  style={{ marginTop: 16 }}
                >
                  <Grid item>
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={submitting}
                      onClick={() => setImageData(null)}
                      startIcon={<RotateLeftIcon />}
                    >
                      {t("documents.retake")}
                    </Button>
                  </Grid>

                  <Grid item>
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={submitting}
                      onClick={() => uploadClick("PhotoFromWebcam")}
                      startIcon={<PublishIcon />}
                    >
                      {t("documents.upload")}
                    </Button>
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      </div>
    </>
  );
};

TakePhoto.propTypes = {
  selectedUploadTypeID: PropTypes.number,
  selectedUploadName: PropTypes.string,
  handleUploadDocument: PropTypes.func.isRequired,
};

export default TakePhoto;

